import React from 'react'

import {
  CatalogCustomAttribute,
  CatalogCustomAttributeType,
  PaginatedMenusCustomAttributeFilter,
  PaginatedMenusCustomAttributeValueFilter
} from 'src/apollo/onlineOrdering'
import ToggleSwitch from 'src/shared/components/common/forms/ToggleSwitch'

import { FilterOptionRows } from './FilterOptionRows'
import { FilterVariant, filterName, matchByIdOrName } from '../filterUtils'

export type CustomAttributeFilterProps = {
  attribute: CatalogCustomAttribute
  variant: FilterVariant
  hasEntered: boolean
  selectedCustomAttributes: PaginatedMenusCustomAttributeFilter[]
  handleCustomAttributeToggle: (
    attribute: { id?: string | null; name: string },
    value: PaginatedMenusCustomAttributeValueFilter
  ) => void
}

const valueKey = (value: { id?: string | null; name: string }): string =>
  value.id ?? value.name

// A boolean attribute's single value is the on/off state, so it renders as one ToggleSwitch. Selection state is derived by the caller.
const BooleanCustomAttribute = ({
  attribute,
  variant,
  isChecked,
  onToggle
}: {
  attribute: CatalogCustomAttribute
  variant: FilterVariant
  isChecked: boolean
  onToggle: () => void
}) => (
  <ToggleSwitch
    id={`${filterName(variant, attribute.name)}-toggle`}
    checked={isChecked}
    onChange={onToggle}
    label={attribute.name}
    labelPosition='left'
    className='filterToggle'
  />
)

// Renders a custom attribute filter: a value toggle-input list (keyed by id-or-name), or a single on/off ToggleSwitch for boolean attributes. The caller supplies the container.
export const CustomAttributeFilter = ({
  attribute,
  variant,
  hasEntered,
  selectedCustomAttributes,
  handleCustomAttributeToggle
}: CustomAttributeFilterProps) => {
  const selectedAttribute = selectedCustomAttributes.find((selectedAttr) =>
    matchByIdOrName(selectedAttr, attribute)
  )
  const isValueSelected = (candidateValue: {
    id?: string | null
    name: string
  }) =>
    selectedAttribute?.values.some((selectedValue) =>
      matchByIdOrName(selectedValue, candidateValue)
    ) ?? false

  if (attribute.type === CatalogCustomAttributeType.Boolean) {
    const booleanValue = attribute.values[0]
    if (!booleanValue) return null
    return (
      <BooleanCustomAttribute
        attribute={attribute}
        variant={variant}
        isChecked={isValueSelected(booleanValue)}
        onToggle={() => handleCustomAttributeToggle(attribute, booleanValue)}
      />
    )
  }

  const valueByKey = new Map(attribute.values.map((v) => [valueKey(v), v]))
  return (
    <FilterOptionRows
      sectionName={attribute.name}
      name={filterName(variant, attribute.name)}
      hasEntered={hasEntered}
      values={attribute.values.map((v) => ({
        id: v.id ?? undefined,
        name: v.name
      }))}
      selectedValues={
        new Set(attribute.values.filter(isValueSelected).map(valueKey))
      }
      onToggle={(key) => {
        const value = valueByKey.get(key)
        if (value) handleCustomAttributeToggle(attribute, value)
      }}
    />
  )
}
